import { Controller } from '@hotwired/stimulus'
import { put } from '@rails/request.js'
import { debouncedFormSubmit } from '~/utils/debounced_form_submit'
import { useEventListener } from '~/controllers/concerns/use_event_listener'

import arrowUp from '~/../assets/images/icons/arrow-up.svg'
import arrowDown from '~/../assets/images/icons/arrow-down.svg'

const orderingInputLeftIcons = {
  'arrow-up': arrowUp,
  'arrow-down': arrowDown
}

export default class extends Controller {
  static targets = ['resetChip', 'orderingInput']

  static values = {
    targetTurboFrame: String,
    spinnerHtml: String,
    updateUrl: String,
    standaloneSearchInput: Boolean
  }

  connect() {
    useEventListener(this, {
      element: window,
      event: 'searchOrderFilterFormUpdate',
      listener: 'searchOrderFilterFormUpdate'
    })
  }

  displayResetChip() {
    if (!this.hasResetChipTarget) {
      return
    }

    if (this.checkedFilters() || this.activeSearch()) {
      this.resetChipTarget.classList.remove('hidden')
    } else {
      this.resetChipTarget.classList.add('hidden')
    }
  }

  checkedFilters() {
    return Array.from(this.filterCheckboxes).some(item => item.checked === true)
  }

  activeSearch() {
    if (this.searchInput === null || this.standaloneSearchInputValue === true) {
      return false
    }

    return this.searchInput.value !== ''
  }

  formSubmit() {
    const form = this.element

    debouncedFormSubmit(form)
    this.displayResetChip()
  }

  reset() {
    this.resetFilters()
    this.resetSearchInput()

    this.displayResetChip()
  }

  resetFilters() {
    this.resetCheckboxes()
    this.resetSearchInputs()
  }

  resetCheckboxes() {
    this.filterCheckboxes.forEach(item => {
      item.checked = false
      this.notifyReset(item)
    })
  }

  resetSearchInputs() {
    this.filterSearchInputs.forEach(item => {
      item.value = ''
      this.notifyReset(item)
    })
  }

  resetSearchInput() {
    if (this.searchInput === null || this.standaloneSearchInputValue === true) {
      return
    }

    this.searchInput.value = ''
    this.notifyReset(this.searchInput, 'input')
  }

  notifyReset(input, type = 'change') {
    input.dispatchEvent(new Event(type, { bubbles: true, composed: true }))
  }

  changeLeftIcon(event) {
    const pictoName = event.currentTarget.dataset.leftIcon
    const newPictoUrl = orderingInputLeftIcons[pictoName]
    const svgContainer = this.orderingInputTarget.querySelector('svg')

    fetch(newPictoUrl)
      .then(response => response.text())
      .then(data => {
        svgContainer.innerHTML = data
      })
      .catch(error => {
        console.error('Error:', error)
      })
  }

  displaySpinner() {
    const turboFrame = document.getElementById(this.targetTurboFrameValue)

    if (!turboFrame) return

    turboFrame.innerHTML = this.spinnerHtmlValue
  }

  get filterCheckboxes() {
    return this.element.querySelectorAll(".custom-select input[type='checkbox']")
  }

  get filterSearchInputs() {
    return this.element.querySelectorAll(".custom-select input[type='search']")
  }

  get searchInput() {
    return this.element.querySelector("input[name*='[search]']")
  }

  async #saveForm() {
    if (!this.hasUpdateUrlValue) {
      return
    }

    // We don't use turboFormSubmit here to work around its debounce time.
    return await put(this.updateUrlValue, {
      responseKind: 'turbo-stream',
      body: new FormData(this.element)
    })
  }

  searchOrderFilterFormUpdate(event) {
    const { changes, submit } = event.detail

    event.stopPropagation()
    Object.values(changes).forEach(change => this.#searchOrderFilterFormApplyChange(change))

    if (submit) {
      return this.formSubmit()
    }
    return this.#saveForm()
  }

  #searchOrderFilterFormApplyChange(change) {
    const nameParts = change.name.split('.')
    const input = this.element.querySelector(`[name$="${nameParts.map(p => `[${p}]`).join('')}"]`)
    if (input === null) {
      return
    }

    input.value = change.value
  }
}
